import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

function LevelBadge({ level, t }) {
  if (level === undefined || level === null) return null;
  const label = t(`memory.levels.${level}`);
  const cls = ["l0", "l1", "l2", "l3", "l4"][level] || "l0";
  return (
    <span className={`tb-mem-lvl tb-mem-lvl-${cls}`} data-testid={`mem-lvl-${level}`} title={label}>
      L{level} · {label}
    </span>
  );
}

function Section({ id, title, count, children, testid }) {
  return (
    <section className="tb-card tb-mem-section" data-testid={testid} id={id}>
      <header className="tb-mem-section-head">
        <h2>{title}</h2>
        {typeof count === "number" && (
          <span className="tb-mem-count" data-testid={`${testid}-count`}>{count}</span>
        )}
      </header>
      <div className="tb-mem-section-body">{children}</div>
    </section>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso.slice(0, 16);
  }
}

export default function Memory() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [undoing, setUndoing] = useState(null);

  const canView = user && (user.role === "admin" || user.features?.journal_enabled === true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/memory/snapshot"),
      api.get("/memory/audit?limit=100"),
    ])
      .then(([snapRes, auditRes]) => {
        setSnap(snapRes.data);
        setAudit(auditRes.data.items || []);
      })
      .catch((e) => setErr(formatApiErrorDetail(e) || "Failed to load memory"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (canView) load();
    else setLoading(false);
  }, [canView]);

  const undo = async (aid) => {
    if (!window.confirm(t("memory.auditConfirmUndo"))) return;
    setUndoing(aid);
    try {
      await api.post(`/memory/audit/${aid}/undo`);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e) || "Undo failed");
    } finally {
      setUndoing(null);
    }
  };

  if (!canView) {
    return (
      <div className="tb-page tb-mem-page" data-testid="memory-page">
        <header className="tb-mem-header">
          <div>
            <h1>{t("memory.title")}</h1>
            <p className="tb-muted">{t("memory.subtitle")}</p>
          </div>
        </header>
        <div className="tb-card">
          <p className="tb-muted">{t("proposals.emptyForRole")}</p>
        </div>
      </div>
    );
  }

  if (loading || !snap) {
    return (
      <div className="tb-page tb-mem-page" data-testid="memory-page">
        <header className="tb-mem-header">
          <div>
            <h1>{t("memory.title")}</h1>
            <p className="tb-muted">{t("memory.subtitle")}</p>
          </div>
        </header>
        <p className="tb-muted">{t("memory.loading")}</p>
      </div>
    );
  }

  const s = snap.sections || {};
  const roadmap = s.roadmap || {};
  const roadmapKeys = ["north_star", "year_10", "year_5", "year_2", "year_1", "month_1", "week_2"];
  const quarters = roadmap.quarters || [];

  return (
    <div className="tb-page tb-mem-page" data-testid="memory-page">
      <header className="tb-mem-header">
        <div>
          <h1>{t("memory.title")}</h1>
          <p className="tb-muted">{t("memory.subtitle")}</p>
        </div>
        <div className="tb-mem-header-actions">
          <small className="tb-muted">
            {t("memory.lastUpdated")}: {fmtDate(snap.generated_at)}
          </small>
        </div>
      </header>

      {err && <div className="tb-error" data-testid="memory-error">{err}</div>}

      <div className="tb-mem-note">
        <span aria-hidden>🧭</span> {t("memory.trustNote")}
      </div>

      <div className="tb-mem-grid">
        {/* 1. Journal */}
        <Section id="journal" title={t("memory.sections.journal")} count={s.journal?.total || 0} testid="mem-journal">
          {(s.journal?.recent || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.journal.recent || []).slice(0, 6).map((e) => (
                <li key={e.id} data-testid={`mem-j-${e.id}`}>
                  <strong>{e.author_name || "—"}</strong>
                  <span className="tb-muted"> · {fmtDate(e.created_at)}</span>
                  <p>{(e.content || "").slice(0, 160)}{(e.content || "").length > 160 ? "…" : ""}</p>
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/journal" className="tb-link" data-testid="mem-journal-link">{t("memory.viewAll")} →</Link>
        </Section>

        {/* 2. Metrics */}
        <Section id="metrics" title={t("memory.sections.metrics")} count={s.metrics?.total || 0} testid="mem-metrics">
          {(s.metrics?.items || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.metrics.items || []).slice(0, 10).map((m) => (
                <li key={m.id} data-testid={`mem-m-${m.id}`}>
                  <div className="tb-mem-row">
                    <strong>{m.label_en || m.key}</strong>
                    <LevelBadge level={m.automation_level ?? 0} t={t} />
                  </div>
                  <span className="tb-muted">
                    {m.value || "—"} {m.unit || ""}
                  </span>
                  {m.notes && <p className="tb-mem-notes">{m.notes.slice(0, 200)}</p>}
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/metrics" className="tb-link" data-testid="mem-metrics-link">{t("memory.viewAll")} →</Link>
        </Section>

        {/* 3. Roadmap */}
        <Section id="roadmap" title={t("memory.sections.roadmap")} testid="mem-roadmap">
          {roadmapKeys.every((k) => !roadmap[k]) && quarters.every((q) => !q) ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {roadmapKeys.map((k) => roadmap[k] && (
                <li key={k}>
                  <strong>{k.replace("_", " ")}</strong>
                  <p>{roadmap[k]}</p>
                </li>
              ))}
              {quarters.map((q, i) => q && (
                <li key={`q${i}`}>
                  <strong>Q{i + 1}</strong>
                  <p>{q}</p>
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/roadmap" className="tb-link" data-testid="mem-roadmap-link">{t("memory.viewAll")} →</Link>
        </Section>

        {/* 4. Documents */}
        <Section id="documents" title={t("memory.sections.documents")} count={s.documents?.total || 0} testid="mem-documents">
          {(s.documents?.items || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.documents.items || []).slice(0, 6).map((d) => (
                <li key={d.id}>
                  <strong>{d.title || d.filename || "Untitled"}</strong>
                  <span className="tb-muted"> · {fmtDate(d.created_at)}</span>
                  {d.has_text && <p className="tb-mem-notes">{d.text_preview}</p>}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* 5. Sessions */}
        <Section id="sessions" title={t("memory.sections.sessions")} count={s.sessions?.total || 0} testid="mem-sessions">
          {(s.sessions?.items || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.sessions.items || []).map((sess) => (
                <li key={sess.id}>
                  <Link to={`/app/sessions/${sess.id}`} className="tb-link">{sess.title || "Session"}</Link>
                  <span className="tb-muted"> · {fmtDate(sess.scheduled_at)} · {sess.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* 6. Mandates */}
        <Section id="mandates" title={t("memory.sections.mandates")} count={s.mandates?.total || 0} testid="mem-mandates">
          {(s.mandates?.items || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.mandates.items || []).map((m) => (
                <li key={m.id}>
                  <Link to={`/app/mandates/${m.id}`} className="tb-link">{m.title}</Link>
                  <span className="tb-muted"> · {m.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* 7. Framework */}
        <Section id="framework" title={t("memory.sections.framework")} testid="mem-framework">
          <ul className="tb-mem-list">
            <li><strong>Stage:</strong> {s.framework?.stage || "—"}</li>
            <li>
              <strong>KTH readiness:</strong>{" "}
              {s.framework?.kth && typeof s.framework.kth === "object"
                ? Object.entries(s.framework.kth)
                    .map(([area, level]) => `${area} L${level}`)
                    .join(" · ") || "—"
                : (s.framework?.kth || "—")}
            </li>
          </ul>
        </Section>

        {/* 8. Proposals */}
        <Section id="proposals" title={t("memory.sections.proposals")} testid="mem-proposals">
          <div className="tb-mem-props-stats">
            <span data-testid="mem-props-pending"><strong>{s.proposals?.pending || 0}</strong> {t("memory.counts.pending")}</span>
            <span data-testid="mem-props-applied"><strong>{s.proposals?.applied || 0}</strong> {t("memory.counts.applied")}</span>
            <span data-testid="mem-props-skipped"><strong>{s.proposals?.skipped || 0}</strong> {t("memory.counts.skipped")}</span>
          </div>
          <Link to="/app/proposals" className="tb-link" data-testid="mem-proposals-link">{t("memory.viewAll")} →</Link>
        </Section>

        {/* 9. Brain */}
        <Section id="brain" title={t("memory.sections.brain")} count={s.brain?.total || 0} testid="mem-brain">
          {(s.brain?.recent || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.brain.recent || []).map((c) => (
                <li key={c.id}>
                  <strong>{c.title || "Untitled"}</strong>
                  <span className="tb-muted"> · {c.message_count || 0} msg · {fmtDate(c.updated_at)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/brain" className="tb-link" data-testid="mem-brain-link">{t("memory.viewAll")} →</Link>
        </Section>

        {/* 10. Decisions */}
        <Section id="decisions" title={t("memory.sections.decisions")} count={s.decisions?.total || 0} testid="mem-decisions">
          {(s.decisions?.items || []).length === 0 ? (
            <p className="tb-muted">{t("memory.empty")}</p>
          ) : (
            <ul className="tb-mem-list">
              {(s.decisions.items || []).slice(0, 8).map((d) => (
                <li key={d.id} data-testid={`mem-d-${d.id}`}>
                  <div className="tb-mem-row">
                    <strong>{d.title}</strong>
                    <LevelBadge level={d.automation_level ?? 1} t={t} />
                  </div>
                  {d.owner && <span className="tb-muted">Owner: {d.owner} · </span>}
                  <span className="tb-muted">{fmtDate(d.decided_at)}</span>
                  {d.context && <p className="tb-mem-notes">{d.context.slice(0, 220)}</p>}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Audit trail */}
      <section className="tb-card tb-mem-audit" data-testid="mem-audit">
        <header className="tb-mem-section-head">
          <h2>{t("memory.auditTitle")}</h2>
          <span className="tb-mem-count">{audit.length}</span>
        </header>
        {audit.length === 0 ? (
          <p className="tb-muted">{t("memory.auditEmpty")}</p>
        ) : (
          <table className="tb-mem-audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Target</th>
                <th>Actor</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} data-testid={`audit-${a.id}`} className={a.undone ? "undone" : ""}>
                  <td>{fmtDate(a.at)}</td>
                  <td>{a.action}</td>
                  <td>
                    <strong>{a.target_display}</strong>
                    <small className="tb-muted"> · {a.target_collection}</small>
                  </td>
                  <td>{a.actor_name}</td>
                  <td className="tb-muted"><small>{a.source}</small></td>
                  <td>
                    {a.undone ? (
                      <span className="tb-muted">{t("memory.auditUndone")}</span>
                    ) : (
                      <button
                        type="button"
                        className="tb-ghost-btn tb-mini"
                        onClick={() => undo(a.id)}
                        disabled={undoing === a.id}
                        data-testid={`audit-undo-${a.id}`}
                      >
                        {undoing === a.id ? "…" : t("memory.auditUndo")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
